import { useEffect, useState } from 'react'
import Icon from '../Icon'
import { useRef } from 'react'
import {
  addSpeciesPhotos, createSpecies, getSpecies, listSpecies, photoUrl, removeSpeciesPhoto,
  searchPalms, updateSpecies, uploadPhoto, type PalmMatch, type Photo, type Species,
} from '../../lib/ppc'

/**
 * Species profiles — the shared biology.
 *
 * ── The plant's NAME is the title ─────────────────────────────────────────────
 *
 * The old build led with identification status, so a perfectly good plant read
 * as an error. Here the name is the heading and the status is a badge beside
 * it. An unconfirmed species is a normal species with a note attached, which is
 * what it actually is, and it is usable everywhere.
 *
 * Confirming asks for the SOURCE, because "how do you know" is the whole
 * difference between a confirmed identification and a confident guess.
 */
const BADGE: Record<Species['id_status'], { label: string; className: string }> = {
  pending: { label: 'Identification pending', className: 'status-pending' },
  ai_suggested: { label: 'AI suggestion — review', className: 'status-ai_suggested' },
  confirmed: { label: 'Confirmed', className: 'status-confirmed' },
}

export default function SpeciesList(
  { canWrite, onBack, onPalms }: { canWrite: boolean; onBack: () => void; onPalms: () => void },
) {
  const [species, setSpecies] = useState<Species[]>([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const reload = () => listSpecies(query || undefined).then(setSpecies).catch(() => {})

  useEffect(() => {
    setLoading(true)
    void listSpecies(query || undefined).then(setSpecies).catch(() => {}).finally(() => setLoading(false))
  }, [query])

  const confirmed = species.filter((s) => s.id_status === 'confirmed').length

  return (
    <>
      <button type="button" className="backlink" onClick={onBack} style={{ marginBottom: 10 }}>
        <Icon name="back" size={14} /> Back to the collection
      </button>

      <div className="pagehead">
        <div>
          <p className="eyebrow">One name, every pot of that kind</p>
          <h1>Learn their names.</h1>
          <p>One species profile connects every plant of that kind.</p>
        </div>
        <div className="actions">
          {/*
            * Open to everyone, not just writers: looking something up in a
            * published checklist changes nothing.
            */}
          <button type="button" onClick={onPalms}>
            <Icon name="book" size={15} /> Palm reference
          </button>
          {canWrite && (
            <button type="button" className="primary" onClick={() => setAdding(true)}>
              <Icon name="plus" size={15} /> Add a species
            </button>
          )}
        </div>
      </div>

      {adding && (
        <SpeciesForm
          onCancel={() => setAdding(false)}
          onSaved={async () => { setAdding(false); await reload() }}
        />
      )}

      <dl className="stats">
        <div><dt>Profiles</dt><dd>{species.length}</dd><small>species and varieties</small></div>
        <div><dt>Confirmed</dt><dd>{confirmed}</dd><small>with a recorded source</small></div>
        <div><dt>Awaiting review</dt><dd>{species.length - confirmed}</dd><small>still usable in inventory</small></div>
        <div><dt>Plants linked</dt><dd>{species.reduce((n, s) => n + (s.plant_count ?? 0), 0)}</dd><small>across all profiles</small></div>
      </dl>

      <div className="searchrow">
        <span className="field">
          <Icon name="search" size={16} />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search species…" aria-label="Search species"
          />
        </span>
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && species.length === 0 && (
        <div className="card empty">
          <Icon name="book" size={30} />
          <h3>No names yet.</h3>
          <p>A species profile is the shared biology — the care, the mature size, the scientific name. Every plant of that kind points at it.</p>
        </div>
      )}

      <div className="stage-grid">
        {species.map((one) => {
          const badge = BADGE[one.id_status]

          if (editingId === one.id) {
            return (
              <SpeciesForm
                key={one.id} existing={one}
                onCancel={() => setEditingId(null)}
                onSaved={async () => { setEditingId(null); await reload() }}
              />
            )
          }

          return (
            <section className="card" key={one.id}>
              {/* Name first. Status is a badge, not the headline. */}
              <h3 style={{ fontSize: 24, margin: '0 0 4px' }}>{one.common_name}</h3>
              {one.scientific_name && (
                <p className="muted" style={{ margin: '0 0 10px' }}>
                  <i>{one.scientific_name}</i>{one.cultivar ? ` ‘${one.cultivar}’` : ''}
                </p>
              )}
              <p style={{ margin: '0 0 12px' }}>
                <span className={`chip ${badge.className}`}>{badge.label}</span>
                {' '}
                <span className="muted small">
                  {one.plant_count ?? 0} plant{one.plant_count === 1 ? '' : 's'}
                </span>
              </p>

              {one.mature_size && <p className="small"><Icon name="ruler" size={14} /> {one.mature_size}</p>}
              {one.care && <p className="small">{one.care}</p>}
              {one.description && <p className="small muted">{one.description}</p>}

              <ReferencePhotos speciesId={one.id} canWrite={canWrite} />

              {/*
               * A species you cannot correct is worse than one you never typed:
               * the wrong name is already on every plant of that kind and on the
               * printed tag. Editing is available at EVERY status, confirmed
               * included — a confirmed identification can still have a
               * misspelt cultivar.
               */}
              {canWrite && (
                <div className="row" style={{ marginTop: 12 }}>
                  <button type="button" onClick={() => setEditingId(one.id)}>
                    <Icon name="edit" size={15} /> Edit
                  </button>
                </div>
              )}

              {canWrite && one.id_status !== 'confirmed' && (
                <ConfirmSpecies one={one} onDone={reload} />
              )}
            </section>
          )
        })}
      </div>
    </>
  )
}

/** Confirming requires saying how you know. */
function ConfirmSpecies({ one, onDone }: { one: Species; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [source, setSource] = useState('')
  const [busy, setBusy] = useState(false)

  if (!open) {
    return <button type="button" onClick={() => setOpen(true)}><Icon name="check" size={15} /> Confirm this identification</button>
  }

  return (
    <div style={{ marginTop: 12 }}>
      <label>
        Identification source / evidence
        <textarea
          value={source} onChange={(e) => setSource(e.target.value)} rows={3}
          placeholder="Original nursery label, expert confirmation, or reference URL and identifying features"
        />
        <span className="hint">A confirmed identification without a source is a guess wearing a badge.</span>
      </label>
      <div className="row">
        <button
          type="button" className="primary" disabled={busy || !source.trim()}
          onClick={async () => {
            setBusy(true)
            await updateSpecies(one.id, {
              idStatus: 'confirmed',
              description: [one.description, `Source: ${source.trim()}`].filter(Boolean).join('\n\n'),
            })
            setBusy(false); setOpen(false); onDone()
          }}
        >
          Confirm
        </button>
        <button type="button" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  )
}

/**
 * One form, two jobs: adding a species and editing one.
 *
 * It is the same set of facts either way, and a second near-identical form is
 * how the two drift until only one of them knows about `mature_size`.
 *
 * ── Editing never changes the identification status ───────────────────────────
 *
 * Correcting a spelling is not evidence. `idStatus` is sent ONLY when creating,
 * and only from the tick-box; moving a species to 'confirmed' stays with
 * ConfirmSpecies, which demands a source first. So an edit cannot quietly
 * promote an AI suggestion to a confirmed identification, which is the one
 * thing this screen must not allow.
 */
function SpeciesForm({ existing, onCancel, onSaved }: {
  existing?: Species
  onCancel: () => void
  onSaved: () => void
}) {
  const editing = !!existing
  const [commonName, setCommonName] = useState(existing?.common_name ?? '')
  const [scientificName, setScientificName] = useState(existing?.scientific_name ?? '')
  const [cultivar, setCultivar] = useState(existing?.cultivar ?? '')
  const [matureSize, setMatureSize] = useState(existing?.mature_size ?? '')
  const [care, setCare] = useState(existing?.care ?? '')
  // Editing shows the description as it stands — which is where a recorded
  // source ends up — rather than a blank "source" box that would append a
  // second one every time someone fixed a typo.
  const [description, setDescription] = useState(existing?.description ?? '')
  const [source, setSource] = useState('')
  const [verified, setVerified] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true); setError(null)
    try {
      if (existing) {
        await updateSpecies(existing.id, {
          commonName: commonName.trim(),
          scientificName: scientificName.trim(),
          cultivar: cultivar.trim(),
          matureSize: matureSize.trim(),
          care: care.trim(),
          description: description.trim(),
        })
      } else {
        await createSpecies({
          commonName: commonName.trim(),
          scientificName: scientificName.trim() || undefined,
          cultivar: cultivar.trim() || undefined,
          matureSize: matureSize.trim() || undefined,
          care: care.trim() || undefined,
          description: source.trim() ? `Source: ${source.trim()}` : undefined,
          // Ticking the box is what confirms it. Nothing else may.
          idStatus: verified ? 'confirmed' : 'pending',
        })
      }
      onSaved()
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <h2 style={{ fontSize: 26, marginBottom: 4 }}>{editing ? 'Edit species' : 'Add a species'}</h2>
      <p className="muted small" style={{ marginTop: 0 }}>
        {editing
          ? 'The shared biology. Every plant of this kind shows what you put here.'
          : 'Confirm a species only when the scientific name and evidence are recorded.'}
      </p>

      <label>
        Common name
        <input value={commonName} onChange={(e) => setCommonName(e.target.value)} />
      </label>

      <div className="two-up">
        <ScientificName
          value={scientificName}
          onChange={setScientificName}
          onPick={(palm) => {
            setScientificName(palm.scientificName)
            // Prefill ONLY what is still empty. Reference data is better than a
            // blank; it is not better than something Dillon typed.
            if (!matureSize && palm.matureSize) setMatureSize(palm.matureSize)
            if (editing && !description && palm.characteristics) setDescription(palm.characteristics)
          }}
        />
        <label>
          Cultivar (if known)
          <input value={cultivar} onChange={(e) => setCultivar(e.target.value)} />
        </label>
      </div>

      <label>
        Mature size
        <input value={matureSize} onChange={(e) => setMatureSize(e.target.value)} placeholder="e.g. 6–8 m, single trunk" />
      </label>

      {editing ? (
        <label>
          Notes, characteristics and identification source
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
        </label>
      ) : (
        <label>
          Identification source / evidence
          <textarea
            value={source} onChange={(e) => setSource(e.target.value)} rows={3}
            placeholder="Original nursery label, expert confirmation, or reference URL and identifying features"
          />
        </label>
      )}

      <label>
        {editing ? 'Care notes' : 'Verified care notes'}
        <textarea value={care} onChange={(e) => setCare(e.target.value)} rows={3} />
      </label>

      {!editing && (
        <label className="checkbox">
          <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
          <span>I have verified this identification.</span>
        </label>
      )}

      {error && <p className="error">{error}</p>}

      <div className="row">
        <button
          type="button" className="primary" disabled={busy || !commonName.trim()}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Save species'}
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </section>
  )
}

/**
 * The scientific-name field, backed by PalmTraits 1.0.
 *
 * Two thousand five hundred and fifty-seven palm species, measured and
 * published. Typing three letters is enough to get the accepted binomial
 * spelled correctly, which matters more here than anywhere else in the app:
 * the scientific name is the thing a customer photographs off the tag and
 * looks up later.
 *
 * Picking a match fills the mature size from the measurement rather than from
 * memory — but only into fields that are still EMPTY, like every other
 * suggestion in this app.
 *
 * It stays a plain text input. A palm Dillon grows that is not in the
 * checklist, or a plant that is not a palm at all, must still be typeable; the
 * list is an offer, never a gate.
 */
function ScientificName({ value, onChange, onPick }: {
  value: string
  onChange: (next: string) => void
  onPick: (palm: PalmMatch) => void
}) {
  const [matches, setMatches] = useState<PalmMatch[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open || value.trim().length < 2) { setMatches([]); return }
    let live = true
    // Debounced: one request per pause, not one per keystroke on mobile signal.
    const timer = setTimeout(() => {
      void searchPalms(value).then((found) => { if (live) setMatches(found) })
    }, 200)
    return () => { live = false; clearTimeout(timer) }
  }, [value, open])

  return (
    <label style={{ position: 'relative' }}>
      Scientific name
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        // A blur that fires before the click would close the list first and
        // swallow the tap — which on a phone reads as the app ignoring you.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Genus species"
        autoCapitalize="off" autoCorrect="off" spellCheck={false}
      />
      {open && matches.length > 0 && (
        <ul className="suggest">
          {matches.map((palm) => (
            <li key={palm.scientificName}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(palm); setOpen(false) }}
              >
                <i>{palm.scientificName}</i>
                {palm.matureSize && <small>{palm.matureSize}</small>}
              </button>
            </li>
          ))}
        </ul>
      )}
      <span className="hint">Palms come from PalmTraits 1.0. Anything else, just type it.</span>
    </label>
  )
}

/**
 * Reference photographs for a species.
 *
 * What the plant looks like when it is doing well, or the leaf detail that
 * separates it from the thing it is always confused with. NOT the pot in the
 * shade house — that belongs on the plant, and keeping them apart is why the
 * collection screen can say "individual pots" and mean it.
 *
 * Loaded on demand rather than with the list: a screen of twenty species
 * should not fetch sixty photographs before it can render.
 */
function ReferencePhotos({ speciesId, canWrite }: { speciesId: string; canWrite: boolean }) {
  const [photos, setPhotos] = useState<Photo[] | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  async function show() {
    setOpen(true)
    if (photos === null) {
      const { photos: got } = await getSpecies(speciesId)
      setPhotos(got)
    }
  }

  async function add(files: FileList | null) {
    if (!files?.length) return
    setBusy(true)
    try {
      const ids: string[] = []
      for (const file of Array.from(files)) ids.push(await uploadPhoto(file, 'species-photo'))
      setPhotos(await addSpeciesPhotos(speciesId, ids))
    } finally {
      setBusy(false)
      if (picker.current) picker.current.value = ''
    }
  }

  if (!open) {
    return (
      <button type="button" className="link tap" onClick={() => void show()} style={{ marginTop: 4 }}>
        Reference photos
      </button>
    )
  }

  return (
    <div style={{ marginTop: 10 }}>
      <div className="row">
        <b className="small">Reference photos</b>
        {canWrite && (
          <button type="button" onClick={() => picker.current?.click()} disabled={busy}>
            <Icon name="image" size={14} /> {busy ? 'Uploading…' : 'Add'}
          </button>
        )}
        <button type="button" className="link tap" onClick={() => setOpen(false)}>Hide</button>
      </div>

      <input
        ref={picker} type="file" accept="image/*" multiple hidden
        onChange={(e) => void add(e.target.files)}
      />

      {photos === null && <p className="muted small">Loading…</p>}
      {photos?.length === 0 && <p className="muted small">None yet.</p>}

      {!!photos?.length && (
        <div className="photo-grid">
          {photos.map((photo) => (
            <figure key={photo.id}>
              <img src={photoUrl(photo)} alt="" loading="lazy" />
              {canWrite && (
                <figcaption>
                  <button
                    type="button" className="link tap danger small"
                    onClick={async () => setPhotos(await removeSpeciesPhoto(speciesId, photo.id))}
                  >
                    Remove
                  </button>
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </div>
  )
}
